import React, { useState } from "react";
import { useLang, translateFr as tfr } from "@/i18n";
import { Calendar as CalIcon } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/utils";

const PRESETS = [
  { v: "today", label: tfr("Aujourd'hui") },
  { v: "yesterday", label: tfr("Hier") },
  { v: "7d", label: tfr("7 derniers jours") },
  { v: "30d", label: tfr("30 derniers jours") },
  { v: "month", label: tfr("Mois en cours") },
  { v: "custom", label: tfr("Personnalisé") },
];

export default function DateRangeFilter({ range, from, to, onChange }) {
  const { t } = useLang();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draft, setDraft] = useState({ from: from ? new Date(from) : undefined, to: to ? new Date(to) : undefined });

  const handlePreset = (v) => {
    if (v === "custom") { setPickerOpen(true); return; }
    onChange({ range: v });
  };

  const applyCustom = () => {
    if (!draft?.from) return;
    onChange({
      range: "custom",
      from: draft.from.toISOString().slice(0, 10),
      to: (draft.to || draft.from).toISOString().slice(0, 10),
    });
    setPickerOpen(false);
  };

  const label = range === "custom" && from
    ? `${formatDate(from)} → ${formatDate(to || from)}`
    : null;

  return (
    <div className="flex items-center gap-1.5" data-testid="date-range-filter">
      <Select value={range} onValueChange={handlePreset}>
        <SelectTrigger className="h-9 w-auto min-w-[150px] text-xs gap-2 border-border bg-card" data-testid="date-range-select">
          <CalIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <SelectValue>{label || (PRESETS.find((p) => p.v === range) && t(`range.${range}`)) || t("range.period")}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {PRESETS.map((p) => (
            <SelectItem key={p.v} value={p.v} data-testid={`date-range-${p.v}`}>{t(`range.${p.v}`)}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
        <PopoverTrigger asChild><span /></PopoverTrigger>
        <PopoverContent align="end" className="w-auto p-3" data-testid="date-range-popover">
          <Calendar mode="range" selected={draft} onSelect={setDraft} numberOfMonths={1} />
          <div className="flex justify-end gap-2 mt-2">
            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setPickerOpen(false)}>{tfr("Annuler")}</Button>
            <Button size="sm" className="h-8 text-xs" onClick={applyCustom} data-testid="date-range-apply">Appliquer</Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
